/**
 * The ONE place the calendar does date math (`080-calendar.md` §1.5).
 *
 * > "The client must **never** use `new Date(y, m, d)` for calendar math — that uses the
 * > browser's local zone. All day-bucketing goes through a single helper module."
 *
 * This is that module. Every instant the server sends is UTC; every instant we render or
 * construct is interpreted in `settings.calendar.timezone` — the CONFIGURED zone, not the
 * browser's. They usually agree; when they do not (travelling, a VM with a wrong clock) the
 * configured one wins, and the difference is exactly one day of every event being wrong.
 *
 * `TZDate` (`@date-fns/tz`, MIT, the official date-fns 4 companion) is a `Date` subclass
 * whose local getters read in a given zone. date-fns 4 propagates the subclass through its
 * functions, so `startOfDay(zoned(iso, tz))` is midnight IN `tz`, and `eachDayOfInterval`
 * walks days in `tz`. That is the whole trick, and it is why no other file in this feature
 * needs to know a timezone exists.
 */
import { TZDate } from '@date-fns/tz';
import { addDays, eachDayOfInterval, format, startOfDay, subMilliseconds } from 'date-fns';
import type { CalendarEvent } from '@/api/endpoints/calendar';

/** `YYYY-MM-DD` in the user's timezone — the identity of a calendar day, and a query param. */
export type DayKey = string;

/** An instant (a UTC ISO-8601 string from the server) as seen in `tz`. */
export function zoned(instant: string | Date | number, tz: string): TZDate {
  const ms = instant instanceof Date ? instant.getTime() : new Date(instant).getTime();
  return new TZDate(ms, tz);
}

/** Which calendar day is this instant on, in `tz`? The only correct way to ask. */
export function dayKeyOf(instant: string | Date | number, tz: string): DayKey {
  return format(zoned(instant, tz), 'yyyy-MM-dd');
}

/**
 * `day` is already IN the user's zone — it came out of `startOfDayKey` or a date-fns function
 * that carried the `TZDate` through. Typed as `Date` because date-fns' generics widen back to
 * `Date` in places; the wall-clock getters are what `format` reads either way.
 */
export function dayKeyOfZoned(day: Date): DayKey {
  return format(day, 'yyyy-MM-dd');
}

export function todayKey(tz: string, now: Date = new Date()): DayKey {
  return dayKeyOf(now, tz);
}

/** Local midnight of `key`, in `tz`, as a `TZDate`. The anchor every view lays out from. */
export function startOfDayKey(key: DayKey, tz: string): TZDate {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  return new TZDate(year, month - 1, day, 0, 0, 0, 0, tz);
}

/** `key` + `days`, in `tz` — DST-safe, because the arithmetic happens on a zoned date. */
export function addDaysToKey(key: DayKey, days: number, tz: string): DayKey {
  return dayKeyOfZoned(addDays(startOfDayKey(key, tz), days));
}

/**
 * A wall-clock time in `tz` → the UTC instant to send to the server.
 *
 * `minutes` is minutes-since-local-midnight, or `null` for an all-day item (which normalizes
 * to local midnight — `080` §1.1). The server normalizes all-day values again on its side;
 * sending midnight is politeness, not the enforcement.
 */
export function toInstant(key: DayKey, minutes: number | null, tz: string): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  const hours = minutes === null ? 0 : Math.floor(minutes / 60);
  const mins = minutes === null ? 0 : minutes % 60;

  // Built from wall-clock COMPONENTS, not by adding milliseconds to midnight: on a DST
  // transition day, 09:00 local is not "midnight + 9h" — it is midnight + 8h or + 10h, and
  // the arithmetic version silently moves the event by an hour twice a year.
  const local = new TZDate(year, month - 1, day, hours, mins, 0, 0, tz);
  return new Date(local.getTime()).toISOString();
}

/** Minutes since local midnight, in `tz`. The inverse of `toInstant`'s `minutes`. */
export function minutesOfDay(instant: string, tz: string): number {
  const local = zoned(instant, tz);
  return local.getHours() * 60 + local.getMinutes();
}

/** `19:00`. Uses the CONFIGURED zone — never `Intl` with the browser's, implicitly (`080` §1.5). */
export function formatTime(instant: string, tz: string): string {
  return format(zoned(instant, tz), 'HH:mm');
}

export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * The END the UI should reason about.
 *
 * `endsAt: null` means two different things, and `080` §1.1 fixes both:
 *  - all-day → a single day, i.e. `startsAt + 1 day` **for display only** (never written back);
 *  - timed   → a point in time, which the week grid renders as a 30-minute block (§4).
 */
export function effectiveEnd(event: CalendarEvent, tz: string): TZDate {
  if (event.endsAt !== null) return zoned(event.endsAt, tz);
  if (event.allDay) return addDays(startOfDay(zoned(event.startsAt, tz)), 1);
  return zoned(event.startsAt, tz);
}

/**
 * Every day an event appears on (`080` §3, "Multi-day events").
 *
 * Note the `−1 ms`: `endsAt` is EXCLUSIVE, so an all-day event ending at midnight on the
 * 18th would otherwise be expanded onto the 18th — a phantom trailing day, on every
 * multi-day event, in every view. This subtraction is the entire defence.
 */
export function eventDayKeys(event: CalendarEvent, tz: string): DayKey[] {
  const start = zoned(event.startsAt, tz);
  const end = effectiveEnd(event, tz);

  const last = end.getTime() > start.getTime() ? subMilliseconds(end, 1) : start;

  return eachDayOfInterval({ start: startOfDay(start), end: startOfDay(last) }).map((day) =>
    format(day, 'yyyy-MM-dd'),
  );
}

/**
 * How many days the event spans — the `3` in `Day 2 of 3` (`080` §3).
 *
 * `080` §1.2's derived rule is `differenceInCalendarDays(endsAt, startsAt)`, which is exactly
 * the number of days the expansion produces. Deriving it FROM the expansion keeps the two
 * from ever disagreeing, which is the failure that puts "Day 2 of 2" on the last day of a
 * three-day fair.
 */
export function spanOf(event: CalendarEvent, tz: string): number {
  return eventDayKeys(event, tz).length;
}

/** Half-open overlap, the client-side twin of the server's predicate (`080` §2.1). */
export function overlaps(
  aStart: number,
  aEnd: number,
  rangeStart: number,
  rangeEnd: number,
): boolean {
  return aStart < rangeEnd && aEnd > rangeStart;
}
